import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, GitBranch, History, Info, TimerOff } from 'lucide-react'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { cn } from '@/lib/utils'
import { formatDurationMinutes as fmt, formatEffortMinutes as effort } from '@/lib/formatDuration'
import { postRecoveryApi, type PostRecoveryRun, type TaskHistoryRow } from './api'
import { formatDate } from '@/lib/dateUtils'

/**
 * One object across every recovery it has ever had — the screen a customer tunes the runbook from.
 *
 * THE COMPARABILITY PROBLEM IS THE SUBJECT, NOT A FOOTNOTE. A runbook is read-only during a
 * recovery, so each run carries a snapshot and edits land on the object runbook afterwards. A task
 * present in four of nine runs was inserted or removed somewhere along the way, and averaging it
 * across all nine averages work that did not exist for five of them. The server has already made
 * that call per task (`presence`) and counted it (`coverage`); this screen's job is to put it in
 * front of the reader BEFORE any average, because an average whose population changed underneath
 * it is not a smaller finding, it is a different one.
 *
 * AND THERE IS ALMOST NO DATA. The estate holds a handful of measured tasks in total, two of them
 * abandoned timers left running for weeks against five-minute plans. So every average is rendered
 * with the sample it rests on, anything under coverage.minObservations is marked as not yet
 * meaningful rather than quietly shown as fact, and excluded outliers are counted on screen. A
 * discarded row that nobody can see is how an average becomes a fiction.
 */

type TaskFilter = 'all' | 'comparable' | 'measured'

/**
 * Task durations are ELAPSED time, so they format as durations and are allowed to roll into days —
 * that is precisely how the seven-week abandoned timer announces itself instead of hiding as
 * "70560m". The proposal totals below are sums ACROSS tasks, which is person-time, and use
 * formatEffortMinutes so the two cannot be read across at each other.
 */
const dur = (m: number | null | undefined) => (m == null ? null : fmt(m))

const EM_DASH = '—'

export function RunbookHistoryView({ objectId }: { objectId: string }) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<TaskFilter>('all')

  const history = useQuery({
    queryKey: ['post-recovery-history', objectId],
    queryFn: () => postRecoveryApi.objectHistory(objectId),
  })

  const data = history.data

  /**
   * Runs oldest-first, each stamped with the revision of the runbook it ran. The letter is derived
   * from runbookFingerprint in chronological order of first appearance, so "rev C" beside a run is
   * the same claim coverage.runbookRevisions makes in the banner — two ways of saying it that
   * cannot disagree, because only one of them is counted here.
   */
  const runs = useMemo(() => {
    const list = [...(data?.runs ?? [])].sort((a, b) => {
      const at = Date.parse(a.actualStart ?? a.plannedStart ?? '') || 0
      const bt = Date.parse(b.actualStart ?? b.plannedStart ?? '') || 0
      return at - bt || a.requestId - b.requestId
    })
    const seen = new Map<string, string>()
    return list.map((run) => {
      const fp = run.runbookFingerprint
      if (fp && !seen.has(fp)) seen.set(fp, String.fromCharCode(65 + seen.size))
      return { run, rev: fp ? seen.get(fp)! : null }
    })
  }, [data?.runs])

  const tasks = data?.tasks ?? []
  const coverage = data?.coverage
  const minObs = coverage?.minObservations ?? 3

  const measuredTaskCount = useMemo(() => tasks.filter((r) => r.observations > 0).length, [tasks])
  const outlierTotal = useMemo(() => tasks.reduce((a, r) => a + r.outlierObservations, 0), [tasks])

  const visible = useMemo(() => {
    const ordered = [...tasks].sort((a, b) => (a.taskNo ?? 9e9) - (b.taskNo ?? 9e9))
    if (filter === 'comparable') return ordered.filter((r) => r.presence === 'comparable')
    if (filter === 'measured') return ordered.filter((r) => r.observations > 0)
    return ordered
  }, [tasks, filter])

  if (history.isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</div>
    )
  }

  if (history.isError || !data) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/5 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
          <p className="text-muted-foreground">
            {t('postRecovery.histLoadFailed',
               'The recovery history for this object could not be read. Nothing is missing from the record — the request failed.')}
          </p>
        </div>
      </div>
    )
  }

  const title = data.object?.description || data.object?.objectId || objectId

  // NEVER RECOVERED IS NOT AN ERROR, and it is not "no data" either — it is a specific, sayable
  // fact with a specific thing that would change it.
  if (data.runCount === 0) {
    return (
      <div className="space-y-4 p-6">
        <Header title={title} objectId={objectId} rtoSeconds={data.object?.rtoSeconds ?? null} />
        <div className="rounded-xl border border-dashed p-8 text-center">
          <History className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
          <p className="mt-3 text-sm font-medium">
            {t('postRecovery.histNeverRecoveredTitle', 'This object has never been recovered')}
          </p>
          <p className="mx-auto mt-1 max-w-xl text-xs text-muted-foreground">
            {t('postRecovery.histNeverRecoveredBody',
               'There is no run to compare against, so there is nothing here to tune the runbook from. The first completed recovery of this object starts the history — planned against actual, task by task.')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 p-6">
      <Header title={title} objectId={objectId} rtoSeconds={data.object?.rtoSeconds ?? null} />

      {/* THE COMPARABILITY STATEMENT COMES FIRST, above every number it qualifies. Put underneath
          the averages it reads as a caveat someone chose to add; put here it is the frame. */}
      {coverage && (
        <div className={cn(
          'rounded-xl border p-4',
          coverage.partial > 0 || coverage.runbookRevisions > 1
            ? 'border-amber-500/40 bg-amber-50 dark:bg-amber-950/30'
            : 'bg-emerald-50 dark:bg-emerald-950/30',
        )}>
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t('postRecovery.histComparable',
                   '{{comparable}} of {{total}} tasks are comparable across all {{runs}} recoveries; {{partial}} appeared in only some runs.',
                   {
                     comparable: coverage.comparable,
                     total: coverage.taskCount,
                     runs: data.runCount,
                     partial: coverage.partial,
                   })}
              </p>
              <p className="text-xs text-muted-foreground">
                {coverage.partial > 0
                  ? t('postRecovery.histComparableWhy',
                       'A task that is not in every run was inserted or removed along the way. Averaging it across all {{runs}} runs would average work that did not exist for some of them, so those rows are set back below and left out of the proposal.',
                       { runs: data.runCount })
                  : t('postRecovery.histComparableAll',
                       'Every task on the runbook was present in every run, so the runs compare like for like on task membership.')}
              </p>
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <GitBranch className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                {coverage.runbookRevisions > 1
                  ? t('postRecovery.histRevisionsMany',
                       'The runbook has been through {{k}} revisions across these recoveries — even the comparable tasks may not be like-for-like work, because a task can be reworded or rescoped without changing which runs it appears in.',
                       { k: coverage.runbookRevisions })
                  : t('postRecovery.histRevisionsOne',
                       'The runbook has not been revised across these recoveries.')}
              </p>
            </div>
          </div>
        </div>
      )}

      {coverage && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('postRecovery.histStatRuns', 'Recoveries')}
                value={String(data.runCount)}
                detail={t('postRecovery.histStatRunsDetail', '{{n}} carried a measured task', {
                  n: runs.filter((r) => r.run.tasksMeasured > 0).length,
                })}
                tint="bg-sky-50 dark:bg-sky-950/40" />
          <Stat label={t('postRecovery.histStatComparable', 'Comparable tasks')}
                value={`${coverage.comparable}/${coverage.taskCount}`}
                detail={t('postRecovery.histStatComparableDetail', '{{n}} present in only some runs', {
                  n: coverage.partial,
                })}
                tint="bg-emerald-50 dark:bg-emerald-950/40" />
          {/* The number that governs everything else on the page, so it gets a card of its own
              rather than a line of small print at the bottom. */}
          <Stat label={t('postRecovery.histStatEnough', 'Tasks with enough measurements')}
                value={`${coverage.withEnoughObservations}/${coverage.taskCount}`}
                detail={t('postRecovery.histStatEnoughDetail',
                          '{{min}} measurements needed before an average means anything', { min: minObs })}
                tone={coverage.withEnoughObservations === 0 ? 'text-amber-700 dark:text-amber-300' : undefined}
                tint="bg-amber-50 dark:bg-amber-950/40" />
          <Stat label={t('postRecovery.histStatMeasured', 'Tasks ever measured')}
                value={`${measuredTaskCount}/${coverage.taskCount}`}
                detail={outlierTotal > 0
                  ? t('postRecovery.histStatOutliers', '{{n}} abandoned timers excluded', { n: outlierTotal })
                  : t('postRecovery.histStatNoOutliers', 'no abandoned timers')}
                tone={outlierTotal > 0 ? 'text-rose-700 dark:text-rose-300' : undefined}
                tint="bg-violet-50 dark:bg-violet-950/40" />
        </div>
      )}

      {/* When nothing has ever been measured, say what would produce a measurement. "No data" tells
          a customer their product is empty; this tells them what to do on the next recovery. */}
      {measuredTaskCount === 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-dashed p-4 text-xs">
          <TimerOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-muted-foreground">
            <span className="font-medium text-foreground">
              {t('postRecovery.histNoMeasuredTitle', 'No measured tasks yet across these {{runs}} recoveries.', { runs: data.runCount })}
            </span>{' '}
            {t('postRecovery.histNoMeasuredBody',
               'A duration is recorded when an engineer moves a task to Started before completing it — a task taken straight to Complete has an outcome but no elapsed time, so there is nothing to average. Everything below is the plan, not the record.')}
          </p>
        </div>
      )}

      <RunStrip runs={runs} />

      <Proposal data={data} minObs={minObs} />

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">
            {t('postRecovery.histTasksTitle', 'Every task, across every recovery')}
          </h3>
          <SegmentedControl
            size="sm"
            ariaLabel={t('postRecovery.histFilterLabel', 'Which tasks to show')}
            value={filter}
            onValueChange={setFilter}
            options={[
              { value: 'all', label: t('postRecovery.histFilterAll', 'All ({{n}})', { n: tasks.length }) },
              { value: 'comparable', label: t('postRecovery.histFilterComparable', 'Comparable ({{n}})', { n: coverage?.comparable ?? 0 }) },
              { value: 'measured', label: t('postRecovery.histFilterMeasured', 'Measured ({{n}})', { n: measuredTaskCount }) },
            ]}
          />
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[1180px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">{t('postRecovery.histColNo', '#')}</th>
                <th className="px-3 py-2 font-medium">{t('postRecovery.histColTask', 'Task')}</th>
                <th className="px-3 py-2 font-medium">{t('postRecovery.histColPresence', 'Presence')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColRuns', 'In runs')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColObs', 'Measured')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColPlanned', 'Planned')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColMin', 'Min')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColAvg', 'Average')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColMax', 'Max')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColSpread', 'Spread')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColWaiting', 'Avg waiting')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColProposed', 'Proposed')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('postRecovery.histColComments', 'Comments')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={13} className="px-3 py-8 text-center text-xs text-muted-foreground">
                    {filter === 'measured'
                      ? t('postRecovery.histNoneMeasured',
                           'No task on this runbook has ever been timed. Durations appear once a task is moved to Started before it is completed.')
                      : filter === 'comparable'
                        ? t('postRecovery.histNoneComparable',
                             'No task appears in all {{runs}} recoveries — the runbook changed between every run, so nothing here is directly comparable.',
                             { runs: data.runCount })
                        : t('postRecovery.histNoTasks', 'No runbook tasks are recorded against these recoveries.')}
                  </td>
                </tr>
              )}
              {visible.map((row) => (
                <TaskRow key={row.taskKey} row={row} runCount={data.runCount} minObs={minObs} />
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-muted-foreground">
          {t('postRecovery.histTableFootnote',
             'Averages exclude abandoned timers, and every one carries the number of measurements behind it. A figure marked n<{{min}} is not yet a finding — it is one engineer, one run.',
             { min: minObs })}
        </p>
      </section>
    </div>
  )
}

function Header({ title, objectId, rtoSeconds }: {
  title: string
  objectId: string
  rtoSeconds: number | null
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">
          {t('postRecovery.histSubtitle',
             '{{id}} — every recovery this object has had, so the runbook and the RTO can be tuned from what actually happened.',
             { id: objectId })}
        </p>
      </div>
      {rtoSeconds != null && rtoSeconds > 0 && (
        <div className="inline-flex items-baseline gap-2 rounded-lg bg-sky-50 px-3 py-1.5 dark:bg-sky-950/40">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {t('postRecovery.histRecordedRto', 'Recorded RTO')}
          </span>
          <span className="text-lg font-semibold tabular-nums">{fmt(Math.round(rtoSeconds / 60))}</span>
        </div>
      )}
    </div>
  )
}

/** Pastel tints, the same four the Readiness cards use — one visual language across the product. */
function Stat({ label, value, detail, tint, tone }: {
  label: string; value: string; detail: string; tint: string; tone?: string
}) {
  return (
    <div className={cn('rounded-xl border p-3', tint)}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-2xl font-semibold tabular-nums', tone)}>{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{detail}</div>
    </div>
  )
}

/**
 * How many recoveries there were and when — deliberately a strip rather than a table. With nine
 * runs at most, the shape of the history is the whole point; the per-run detail lives on the run
 * screen and repeating it here would bury the one thing this is for.
 */
function RunStrip({ runs }: {
  runs: { run: PostRecoveryRun; rev: string | null }[]
}) {
  const { t } = useTranslation()
  // runCount and runs come from different queries server-side; a heading over an empty strip would
  // be the screen quietly disagreeing with the count it just printed.
  if (runs.length === 0) return null
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">
        {t('postRecovery.histRunsTitle', 'The recoveries, oldest first')}
      </h3>
      <div className="overflow-x-auto">
        <div className="flex min-w-min gap-2 pb-1">
          {runs.map(({ run, rev }) => {
            const when = run.actualStart ?? run.plannedStart
            return (
              <div key={run.requestId}
                   className="w-40 shrink-0 rounded-lg border bg-card p-2.5 text-xs"
                   title={run.description ?? undefined}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium tabular-nums">#{run.requestId}</span>
                  {rev && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {t('postRecovery.histRev', 'rev {{r}}', { r: rev })}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {when ? formatDate(when) : t('postRecovery.histNoDate', 'no date recorded')}
                </div>
                <div className="mt-1.5 tabular-nums">
                  {run.actualElapsedMinutes != null
                    ? fmt(run.actualElapsedMinutes)
                    : <span className="text-muted-foreground">{EM_DASH}</span>}
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {t('postRecovery.histRunMeasured', '{{m}} of {{n}} tasks timed', {
                    m: run.tasksMeasured, n: run.taskCount,
                  })}
                </div>
                {run.tasksOutlier > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-800 dark:bg-rose-950/60 dark:text-rose-300">
                    <TimerOff className="h-3 w-3" aria-hidden />
                    {t('postRecovery.histRunOutlier', '{{n}} abandoned', { n: run.tasksOutlier })}
                  </div>
                )}
                {!run.isFinished && (
                  <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">
                    {t('postRecovery.histRunUnfinished', 'not finished')}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/**
 * A PROPOSAL, never a correction. The numbers here rest on a handful of measurements, and the
 * distance between "the evidence suggests 40 minutes" and "the runbook is wrong" is the whole
 * credibility of the feature — so the panel names how many tasks actually moved, and there is no
 * apply button anywhere on this screen. A person decides.
 */
function Proposal({ data, minObs }: {
  data: { proposal: { plannedTotalMinutes: number; proposedTotalMinutes: number; basedOnTasks: number }
          coverage: { taskCount: number } }
  minObs: number
}) {
  const { t } = useTranslation()
  const { plannedTotalMinutes, proposedTotalMinutes, basedOnTasks } = data.proposal

  if (basedOnTasks === 0) {
    return (
      <section className="rounded-xl border border-dashed p-4">
        <h3 className="text-sm font-semibold">{t('postRecovery.histProposalTitle', 'Proposed values')}</h3>
        <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
          {t('postRecovery.histProposalNone',
             'No proposal yet. A task needs {{min}} measurements before its timing can be proposed from evidence, and no task on this runbook has that many. The planned total stays at {{planned}} — untouched, because guessing from one observation would be worse than leaving the plan alone.',
             { min: minObs, planned: effort(plannedTotalMinutes) })}
        </p>
      </section>
    )
  }

  const delta = proposedTotalMinutes - plannedTotalMinutes
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : ''

  return (
    <section className="rounded-xl border p-4">
      <h3 className="text-sm font-semibold">{t('postRecovery.histProposalTitle', 'Proposed values')}</h3>
      <div className="mt-3 flex flex-wrap items-end gap-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('postRecovery.histProposalPlanned', 'Planned today')}
          </div>
          <div className="text-2xl font-semibold tabular-nums">{effort(plannedTotalMinutes)}</div>
        </div>
        <div className="text-xl text-muted-foreground">→</div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('postRecovery.histProposalProposed', 'Proposed')}
          </div>
          <div className="text-2xl font-semibold tabular-nums">{effort(proposedTotalMinutes)}</div>
        </div>
        <div className={cn(
          'rounded-lg px-3 py-1.5',
          delta > 0 ? 'bg-rose-50 dark:bg-rose-950/40'
            : delta < 0 ? 'bg-emerald-50 dark:bg-emerald-950/40'
              : 'bg-muted/40',
        )}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('postRecovery.histProposalDelta', 'Difference')}
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {delta === 0 ? t('postRecovery.histProposalNoChange', 'no change') : `${sign}${effort(Math.abs(delta))}`}
          </div>
        </div>
      </div>

      <p className="mt-3 max-w-3xl text-xs text-muted-foreground">
        {t('postRecovery.histProposalBasis',
           'This rests on {{n}} of {{total}} tasks — the only ones with {{min}} or more measurements. Every other task is carried through at its planned value, so most of the total above is still the plan.',
           { n: basedOnTasks, total: data.coverage.taskCount, min: minObs })}
      </p>
      {/* The same trap ResourceOverview names: a sum across tasks is not an elapsed recovery, and
          quoting one against the other is how a screen comes to contradict itself. */}
      <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
        {t('postRecovery.histProposalCaveat',
           'Both totals are sums of task durations, not the elapsed recovery — tasks that run alongside each other are counted twice over. Read the difference, not the totals, against the RTO. Nothing here is saved: this screen proposes, a person decides.')}
      </p>
    </section>
  )
}

function TaskRow({ row, runCount, minObs }: {
  row: TaskHistoryRow
  runCount: number
  minObs: number
}) {
  const { t } = useTranslation()
  const partial = row.presence === 'partial'
  const thin = row.observations > 0 && row.observations < minObs

  return (
    <tr className={cn(
      'border-b align-top last:border-0 hover:bg-muted/30',
      // A muted left border rather than greyed text: the row must still be readable, it just must
      // not be the first thing the eye lands on. Comparable rows carry a transparent border of the
      // same width so the two do not sit at different indents.
      partial ? 'border-l-2 border-l-muted-foreground/30 bg-muted/20' : 'border-l-2 border-l-transparent',
    )}>
      <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{row.taskNo ?? EM_DASH}</td>
      <td className="max-w-[280px] px-3 py-2">
        <div className="truncate" title={row.task ?? undefined}>
          {row.task || <span className="text-muted-foreground">{t('postRecovery.histUnnamedTask', 'unnamed task')}</span>}
        </div>
      </td>
      <td className="px-3 py-2">
        <span className={cn(
          'whitespace-nowrap rounded px-1.5 py-0.5 text-[10px]',
          partial
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
            : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300',
        )}>
          {partial
            ? t('postRecovery.histPartial', 'some runs')
            : t('postRecovery.histComparableChip', 'every run')}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums">
        {row.runsWithTask}/{runCount}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs">
        <span className="tabular-nums">{row.observations}</span>
        {/* An excluded observation is stated, never dropped. Two of the three measurements in the
            whole estate are abandoned timers; if they vanish silently the remaining average looks
            like a well-sampled figure instead of the single reading it is. */}
        {row.outlierObservations > 0 && (
          <div className="mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
               title={t('postRecovery.histOutlierWhy',
                        'A timer left running long past the plan — usually a task started and never closed. Included, it would dominate the average outright.')}>
            <TimerOff className="h-3 w-3" aria-hidden />
            {t('postRecovery.histOutlierChip', '{{n}} abandoned excluded', { n: row.outlierObservations })}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums">
        {dur(row.plannedMinutes) ?? <span className="text-muted-foreground">{EM_DASH}</span>}
      </td>
      <Measured value={row.minActualMinutes} thin={thin} />
      <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs',
                        thin && 'text-muted-foreground')}>
        {row.observations === 0 ? (
          <span className="text-muted-foreground" title={t('postRecovery.histNoObsWhy',
            'Never timed: no run of this task was moved to Started before it was completed, so there is no elapsed duration to average.')}>
            {EM_DASH}
          </span>
        ) : (
          <span className="inline-flex items-center justify-end gap-1.5">
            <span className="font-medium tabular-nums">{dur(row.avgActualMinutes) ?? EM_DASH}</span>
            {/* The sample travels with the average, always — this cell is the one someone quotes. */}
            <span className={cn(
              'rounded px-1 py-0.5 text-[10px] tabular-nums',
              thin
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                : 'bg-muted text-muted-foreground',
            )}
                  title={thin
                    ? t('postRecovery.histThinWhy',
                        'Below the {{min}} measurements needed for an average to mean anything. Treat this as one reading, not a finding.',
                        { min: minObs })
                    : undefined}>
              {t('postRecovery.histObsMarker', 'n={{n}}', { n: row.observations })}
            </span>
          </span>
        )}
        {row.observations === 0 && (
          <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
            {t('postRecovery.histNeverTimed', 'never timed')}
          </div>
        )}
      </td>
      <Measured value={row.maxActualMinutes} thin={thin} />
      <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums',
                        thin && 'text-muted-foreground')}>
        {/* A standard deviation from one or two readings is arithmetic, not spread. It is shown
            only where there is something for it to describe. */}
        {row.observations >= 2 && row.stdDevActualMinutes != null
          ? `± ${fmt(row.stdDevActualMinutes)}`
          : <span className="text-muted-foreground">{EM_DASH}</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums">
        {dur(row.avgWaitingMinutes) ?? <span className="text-muted-foreground">{EM_DASH}</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums">
        {row.proposedMinutes != null
          ? <span className="font-medium">{fmt(row.proposedMinutes)}</span>
          : <span className="text-muted-foreground"
                  title={t('postRecovery.histNoProposal',
                           'Not enough measurements to propose a value — this task keeps its planned duration.')}>
              {EM_DASH}
            </span>}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
        {row.commentCount || EM_DASH}
      </td>
    </tr>
  )
}

/** Min and max share a cell shape: a duration, muted when the sample behind it is too thin to lean on. */
function Measured({ value, thin }: { value: number | null; thin: boolean }) {
  return (
    <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums',
                      thin && 'text-muted-foreground')}>
      {dur(value) ?? <span className="text-muted-foreground">{EM_DASH}</span>}
    </td>
  )
}
